import { TextBadge, type TextBadgeProps } from '@carvana/ds-badge';
import { decorative } from '@carvana/ds-styles';
import { Clock, type LucideProps } from 'lucide-react';
import type { FunctionComponent } from 'react';
import useUnlockTimer, {
  formatTimeRemaining
} from '~/hooks/home/useUnlockTimer';

export enum StatusTagEnum {
  Hold = 'hold',
  Locked = 'locked',
  PreOrdered = 'preOrdered',
  Recent = 'recent',
  Reservable = 'reservable',
  Sold = 'sold',
  Unavailable = 'unavailable'
}

type StatusConfig = {
  customBorderColor?: string;
  icon?: FunctionComponent<LucideProps>;
  priority?: TextBadgeProps['priority'];
  showTimer?: boolean;
  text: string;
};

const STATUS_CONFIG: Record<StatusTagEnum, StatusConfig> = {
  [StatusTagEnum.Hold]: {
    icon: () => <Clock size={16} />,
    priority: 'warning',
    showTimer: true,
    text: 'On hold'
  },
  [StatusTagEnum.Locked]: {
    priority: 'critical',
    text: 'Purchase in progress'
  },
  [StatusTagEnum.PreOrdered]: {
    customBorderColor: decorative.preOrder,
    priority: 'custom',
    text: 'Pre-ordered'
  },
  [StatusTagEnum.Recent]: {
    customBorderColor: decorative['4Strong'],
    priority: 'custom',
    text: 'Recent'
  },
  [StatusTagEnum.Reservable]: {
    customBorderColor: decorative.preOrder,
    priority: 'custom',
    text: 'Pre-order now'
  },
  [StatusTagEnum.Sold]: {
    priority: 'muted',
    text: 'Sold'
  },
  [StatusTagEnum.Unavailable]: {
    priority: 'critical',
    text: 'Unavailable'
  }
};

type OwnProps = {
  status: StatusTagEnum | null;
  vehicleUnlockDateTime?: string;
};

export default function StatusTag({ status, vehicleUnlockDateTime }: OwnProps) {
  const timeLeft = useUnlockTimer(vehicleUnlockDateTime);

  if (!status) return null;

  const config = STATUS_CONFIG[status];
  const timeRemaining =
    config.showTimer && timeLeft ? formatTimeRemaining(timeLeft) : null;

  return (
    <TextBadge
      customBorderColor={config.customBorderColor}
      customFontColor={
        config.customBorderColor ? 'text-text-strong' : undefined
      }
      iconLeft={config.icon}
      priority={config.priority}
      variant="outline"
      data-testid={`status-tag-${status}`}
    >
      {config.text}
      {timeRemaining && (
        <span className="tabular-nums" data-testid="status-tag-timer">
          {' '}
          {timeRemaining}
        </span>
      )}
    </TextBadge>
  );
}
